import { type Participant } from '@databases/utils/presence/types';

import {
  type ComponentRegistry,
  type PresenceComponentEntry,
} from './contracts/component-registry';
import { type PresenceStateInterface } from './contracts/presence-state-interface';
import { type FieldEntryGrouping } from './types';
import { getGrouping, getKey } from './utils';

export class PresenceState implements PresenceStateInterface {
  /**
   * Participants will be stored in a map where the key is "<fieldId>-<entryId>". One of these ids can be
   * undefined representing an entire field/entry is selected. For example, if the key is just "-<entryId>", it
   * means the entire entry is selected.
   */
  #participants: Map<string, Participant[]> = new Map();

  /** Key is "<accountId>" and value is the "<fieldId>-<entryId>" hash. */
  #participantsByAccountId: Map<string, string> = new Map();
  #componentRegistry: ComponentRegistry<PresenceComponentEntry>;

  constructor(componentRegistry: ComponentRegistry<PresenceComponentEntry>) {
    this.#componentRegistry = componentRegistry;
  }

  addParticipant(grouping: FieldEntryGrouping, participant: Participant): void {
    this.removeParticipant(participant);

    const key = getKey(grouping);

    if (!this.#participants.has(key)) {
      this.#participants.set(key, []);
    }

    this.#participantsByAccountId.set(participant.identity.accountId, key);
    this.#participants.get(key)?.unshift(participant);
    this.#notifyRegisteredComponents(grouping);
  }

  getParticipantsFromGrouping(grouping: FieldEntryGrouping): Participant[] {
    const key = getKey(grouping);
    const participants = this.#participants.get(key);

    return participants ?? [];
  }

  get groupingToParticipantsMap() {
    return this.#participants;
  }

  get accountIdToParticipantsMap() {
    return this.#participantsByAccountId;
  }

  getRecentCollaboratorColor(grouping: FieldEntryGrouping): string | undefined {
    const participants = this.getParticipantsFromGrouping(grouping);
    return participants.length > 0 ? participants[0].colorToken : undefined;
  }

  removeParticipant(participant: Participant): void {
    const key = this.#participantsByAccountId.get(participant.identity.accountId);

    if (!key) {
      return;
    }

    // remove participant from old cell
    const participants = this.#participants.get(key);
    const newParticipants = participants?.filter(
      (p) => p.identity.accountId !== participant.identity.accountId
    );

    if (participants && newParticipants) {
      this.#participants.set(key, newParticipants);
    }

    // if no participants left, remove the key
    if (newParticipants?.length === 0) {
      this.#participants.delete(key);
    }

    this.#participantsByAccountId.delete(participant.identity.accountId);
    this.#notifyRegisteredComponents(getGrouping(key));
  }

  #notifyRegisteredComponents(grouping: FieldEntryGrouping) {
    const updaters = this.#componentRegistry.getComponent(grouping);

    if (updaters) {
      const key = getKey(grouping);
      const allParticipants = this.#participants.get(key) || [];

      updaters.forEach((updater) => {
        updater.updater(allParticipants);
      });
    }
  }
}
